import { ExportTypeEnum } from "@app/operations/exportTypeEnum";
import { JobCompletionGranularity } from "@app/operations/job-completion-rate/types";
import { WeeklyJobStatisticsResult, HumanaJobStatisticsResult, JobCompletionCountResult, JobStatusesResult, ProviderSetCountResult, ProviderSetVersionResult, FullJobDetailsByStatusResponse, WeekendRunProjectStatusesResult, HumanaReportInfoResult, WeekendRunFailedJobsResult, WeekendRunHungJobsResult, UHCJobStatisticsResult } from "@app/operations/jobStatisticsResult";
import { EnumerationItemModel } from "@app/shared/interfaces/enumerationItemModel";
import ServiceBase from "@app/shared/serviceBase";

export default class OperationsService extends ServiceBase {
  public async getHumanaJobStatistics(weekStartDate: string, userId: number, clientId: number): Promise<HumanaJobStatisticsResult[]> {
    return await this.makeGet<HumanaJobStatisticsResult[]>(`/operations/job-statistics?weekStartDate=${weekStartDate}&userId=${userId}&clientId=${clientId}`);
  }

  public async getUHCJobStatistics(weekStartDate: string, lineOfBusiness: string, clientId: number): Promise<UHCJobStatisticsResult[]> {
    return await this.makeGet<UHCJobStatisticsResult[]>(`/operations/uhc-job-statistics?weekStartDate=${weekStartDate}&clientId=${clientId}&lineOfBusiness=${lineOfBusiness}`);
  }

  public async getJobTypes(signal?: AbortSignal): Promise<EnumerationItemModel[]> {
    return await this.makeGet<EnumerationItemModel[]>("/operations/job-types", undefined, undefined, false, undefined, signal);
  }

  public async getJobCompletionCounts(clientId: number, jobType: number, startDate: string, endDate: string, granularity: JobCompletionGranularity, signal?: AbortSignal): Promise<JobCompletionCountResult[]> {
    return await this.makeGet<JobCompletionCountResult[]>("/operations/job-completion-counts", { clientId, endDate, granularity, jobType, startDate }, undefined, false, undefined, signal);
  }

  public async getWeeklyJobStatistics(clientId: number, lineOfBusiness: string, runDate: string): Promise<WeeklyJobStatisticsResult[]> {
    const requestPayload = {
      ClientId: clientId,
      LineOfBusiness: lineOfBusiness,
      RunDate: runDate,
    };

    return await this.makePost<WeeklyJobStatisticsResult[]>("/operations/weekly-job-statistics", requestPayload);
  }

  public async getJobStatuses(clientId: number, runDate: string, lineOfBusiness: string, jobType: number): Promise<JobStatusesResult> {
    const requestPayload = {
      ClientId: clientId,
      JobType: jobType,
      LineOfBusiness: lineOfBusiness,
      RunDate: runDate,   
    };

    return await this.makePost<JobStatusesResult>("/operations/job-statuses", requestPayload);
  }

  public async getLinesOfBusiness(clientId: number, isUHC?: boolean): Promise<string[]> {
    return await this.makeGet<string[]>("/operations/lines-of-business", {
      clientId,
      isUHC
    });
  }

  public async getProjectStatusesByClient(clientId: number, runDate: string, lineOfBusiness: string, limitRunDate?: string): Promise<WeekendRunProjectStatusesResult[]> {
    return await this.makeGet<WeekendRunProjectStatusesResult[]>("/operations/weekend-run-projects", { clientId, limitRunDate, lineOfBusiness, runDate });
  }

  public async getUnfinishedProjectNames(runDate: string, clientId: number, lineOfBusiness: string): Promise<{ projectName: string }[]> {
    return await this.makeGet<Array<{ projectName: string }>>(`/operations/unfinished-projects?runDate=${runDate}&clientId=${clientId}&lineOfBusiness=${lineOfBusiness}`);
  }

  public async getFullJobDetailsByStatus(runDate: string, clientId: number, jobStatus: string, lineOfBusiness: string, jobType: number): Promise<FullJobDetailsByStatusResponse[]> {
    return await this.makeGet<FullJobDetailsByStatusResponse[]>(`/operations/full-job-details?runDate=${runDate}&clientId=${clientId}&jobStatus=${jobStatus}&lineOfBusiness=${lineOfBusiness}&jobType=${jobType}`);
  }

  public async getProviderSetCounts(clientId: number, dateCreated: string): Promise<ProviderSetCountResult[]> {
    return await this.makeGet<ProviderSetCountResult[]>(`/operations/provider-set-counts?dateCreated=${dateCreated}&clientId=${clientId}`);
  }

  public async getProviderSetVersions(clientId: number, dateCreated: string, isActive: boolean): Promise<ProviderSetVersionResult[]> {
    return await this.makeGet<ProviderSetVersionResult[]>(`/operations/provider-set-versions?dateCreated=${dateCreated}&clientId=${clientId}&isActive=${isActive}`);
  }

  public async getAllClients(): Promise<{ clientId: number, name: string }[]> {
    return await this.makeGet<{ clientId: number, name: string }[]>("/clients");
  }

  public async getUsersByClient(clientId: number): Promise<{ userId: number, userName: string }[]> {
    return await this.makeGet<{ userId: number, userName: string }[]>(`/users/client/${clientId}`);
  }

  public async getFailedJobsByClient(clientId: number, runDate: string): Promise<WeekendRunFailedJobsResult[]> {
    return await this.makeGet<WeekendRunFailedJobsResult[]>(`/operations/weekend-run-failed-jobs?clientId=${clientId}&runDate=${runDate}`);
  }

  public async getHungJobsByClient(clientId: number, runDate: string): Promise<WeekendRunHungJobsResult[]> {
    return await this.makeGet<WeekendRunHungJobsResult[]>(`/operations/weekend-run-hung-jobs?clientId=${clientId}&runDate=${runDate}`);
  }

  public async getMinimalUsersByClient(clientId: number): Promise<{ userId: number, userName: string }[]> {
    return await this.makeGet<{ userId: number, userName: string }[]>(`/users/client/${clientId}/minimal`);
  }

  public async getExportStatusesByClient(clientId: number, userId: number, runDate: string, limitRunDate: string, exportTypes: ExportTypeEnum[] ): Promise<HumanaReportInfoResult[]> {
    const requestPayload = {
      ClientId: clientId,
      ExportTypes: exportTypes,
      LimitRunDate: limitRunDate,
      RunDate: runDate,
      UserId: userId,
    };
    return await this.makePost<HumanaReportInfoResult[]>("/operations/weekend-run-humana-exports", requestPayload);
  }
}